import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
    Upload,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader2,
    AlertTriangle,
    X,
} from 'lucide-react';
import { apiClient } from '../../services/api';
import SendConsentAfterImportModal from './SendConsentAfterImportModal';

interface CSVImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImportComplete: () => void;
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({ isOpen, onClose, onImportComplete }) => {
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [validationResult, setValidationResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showWarning, setShowWarning] = useState(true);

    const [showConsentModal, setShowConsentModal] = useState(false);
    const [importedDriverIds, setImportedDriverIds] = useState<string[]>([]);
    const [importStats, setImportStats] = useState<{
        totalImported: number;
        driversWithEmail: number;
        driversWithoutEmail: number;
    } | null>(null);
    const [isSendingConsent, setIsSendingConsent] = useState(false);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFile(null);
            setIsDragging(false);
            setIsUploading(false);
            setIsImporting(false);
            setValidationResult(null);
            setError(null);
            setShowConfirmation(false);
            setShowWarning(true);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && droppedFile.name.endsWith('.csv')) {
            handleFileSelect(droppedFile);
        } else {
            setError('Please upload a valid CSV file');
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            handleFileSelect(selectedFile);
        }
    };

    const handleFileSelect = async (selectedFile: File) => {
        setFile(selectedFile);
        setError(null);
        setValidationResult(null);
        setShowConfirmation(false);
        setShowWarning(true);
        try {
            setIsUploading(true);
            const response = await apiClient.uploadCSV(selectedFile);
            setValidationResult(response.data);
            if (response.data.success) {
                setShowConfirmation(true);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to validate CSV file');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const blob = await apiClient.downloadCSVTemplate();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'driver-import-template.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            setError(err.message || 'Failed to download template');
        }
    };

    const handleConfirmImport = async () => {
        if (!validationResult?.preview) return;
        try {
            setIsImporting(true);
            setError(null);
            const response = await apiClient.importDriversFromCSV({ drivers: validationResult.data });
            const operationId = response.data.operationId;
            toast.success(`Import started! Estimated time: ${response.data.estimatedTime}`, { duration: 5000 });
            setFile(null);
            setValidationResult(null);
            setShowConfirmation(false);
            onImportComplete();
            await pollImportStatus(operationId);
            pollingIntervalRef.current = setInterval(() => {
                pollImportStatus(operationId);
            }, 5000);
        } catch (err: any) {
            setError(err.message || 'Failed to start import');
            setFile(null);
            setValidationResult(null);
            setShowConfirmation(false);
        } finally {
            setIsImporting(false);
        }
    };

    const handleCancel = () => {
        setFile(null);
        setValidationResult(null);
        setShowConfirmation(false);
        setError(null);
        setShowWarning(true);
    };

    const pollImportStatus = async (operationId: string) => {
        try {
            const statusResponse = await apiClient.getBulkOperationStatus(operationId);
            if (statusResponse.data.isComplete) {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                const resultsResponse = await apiClient.getBulkOperationResults(operationId);
                const items = resultsResponse.data.items || [];
                const driverIds: string[] = [];
                let withEmail = 0;
                let withoutEmail = 0;
                for (const item of items) {
                    if (item.driver) {
                        driverIds.push(item.driver.id);
                        if (item.driver.email && item.driver.email.trim() !== '') {
                            withEmail++;
                        } else {
                            withoutEmail++;
                        }
                    }
                }
                if (withEmail === 0) {
                    toast('No drivers have email addresses. Consent forms cannot be sent.', { icon: 'ℹ️', duration: 5000 });
                    return;
                }
                setImportedDriverIds(driverIds);
                setImportStats({ totalImported: driverIds.length, driversWithEmail: withEmail, driversWithoutEmail: withoutEmail });
                setShowConsentModal(true);
            }
        } catch (error) {
            console.error('Error polling import status:', error);
        }
    };

    const handleSendConsentForms = async () => {
        try {
            setIsSendingConsent(true);
            await apiClient.startBulkConsentSend({ driverIds: importedDriverIds, includePending: false, includeValid: false });
            setShowConsentModal(false);
            toast.success('Sending consent forms... You\'ll be notified when complete.');
            setImportedDriverIds([]);
            setImportStats(null);
        } catch (err: any) {
            toast.error(err.message || 'Failed to send consent forms');
        } finally {
            setIsSendingConsent(false);
        }
    };

    return (
        <>
            {/* Modal backdrop */}
            <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="fixed inset-0 bg-black/50" onClick={onClose} />
                <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <Upload className="w-5 h-5 text-green-700" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Import Drivers from CSV</h2>
                                <p className="text-sm text-gray-500">Upload a CSV file to bulk import drivers</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-5">
                        {/* Download Template */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                                <div className="flex items-start">
                                    <FileText className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-medium text-blue-900 mb-1">Need a template?</p>
                                        <p className="text-sm text-blue-800">
                                            Download our CSV template to ensure your data is formatted correctly.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleDownloadTemplate}
                                    className="px-3 py-1.5 text-sm text-blue-700 hover:text-blue-900 font-medium whitespace-nowrap"
                                >
                                    Download Template
                                </button>
                            </div>
                        </div>

                        {/* File Upload Area */}
                        {!validationResult && (
                            <div
                                className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors ${
                                    isDragging ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'
                                }`}
                                onDragEnter={handleDragEnter}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                                <h3 className="text-base font-medium text-gray-900 mb-1">
                                    {isDragging ? 'Drop your CSV file here' : 'Upload CSV File'}
                                </h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Drag and drop your CSV file here, or click to browse
                                </p>
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                    id="csv-upload-modal"
                                    disabled={isUploading}
                                />
                                <label
                                    htmlFor="csv-upload-modal"
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md cursor-pointer hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    {isUploading ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Validating...</>
                                    ) : (
                                        <><Upload className="w-4 h-4 mr-2" />Choose File</>
                                    )}
                                </label>
                                {file && (
                                    <p className="mt-2 text-sm text-gray-500">Selected: {file.name}</p>
                                )}
                            </div>
                        )}

                        {/* Error Display */}
                        {error && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-center">
                                    <AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
                                    <span className="text-sm text-red-800">{error}</span>
                                </div>
                            </div>
                        )}

                        {/* Validation Errors */}
                        {validationResult && !validationResult.success && showWarning && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start flex-1">
                                        <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <h4 className="text-sm font-medium text-red-900 mb-2">Validation Failed</h4>
                                            {validationResult.errors && validationResult.errors.length > 0 && (
                                                <div className="mb-3">
                                                    <p className="text-sm font-medium text-red-800 mb-1">Errors:</p>
                                                    <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                                                        {validationResult.errors.map((err: any, idx: number) => (
                                                            <li key={idx}>
                                                                {typeof err === 'string' ? err : `Row ${err.row}: ${err.field} - ${err.message}`}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {validationResult.duplicates && validationResult.duplicates.length > 0 && (
                                                <div>
                                                    <p className="text-sm font-medium text-red-800 mb-1">Duplicate licence numbers found:</p>
                                                    <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                                                        {validationResult.duplicates.map((lic: string, idx: number) => (
                                                            <li key={idx}>{lic}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <button onClick={handleCancel} className="ml-4 text-red-400 hover:text-red-600">
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                                <div className="mt-3">
                                    <button onClick={handleCancel} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md">
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Preview and Confirmation */}
                        {showConfirmation && validationResult && validationResult.success && (
                            <div className="space-y-4">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <h4 className="text-sm font-medium text-green-900 mb-1">CSV Validated Successfully</h4>
                                            <p className="text-sm text-green-800">{validationResult.validRows} valid driver records found</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Preview Table */}
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                        <h4 className="text-sm font-medium text-gray-900">Preview (first 5 rows)</h4>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Licence</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">DOB</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Site</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {validationResult.preview?.map((driver: any, idx: number) => (
                                                    <tr key={idx}>
                                                        <td className="px-4 py-2 text-sm text-gray-900">{driver.firstName} {driver.lastName}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-900">{driver.licenceNumber}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-500">{driver.email || '—'}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-500">{driver.dateOfBirth}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-500">
                                                            {driver.siteCode ? (
                                                                <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs">{driver.siteCode}</span>
                                                            ) : (
                                                                <span className="text-gray-400 italic text-xs">Unassigned</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={handleCancel}
                                        disabled={isImporting}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirmImport}
                                        disabled={isImporting}
                                        className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
                                        style={{ backgroundColor: '#69BE28' }}
                                    >
                                        {isImporting ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" />Importing...</>
                                        ) : (
                                            `Import ${validationResult.validRows} Drivers`
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Consent Request Modal */}
            <SendConsentAfterImportModal
                isOpen={showConsentModal}
                onClose={() => {
                    setShowConsentModal(false);
                    setImportedDriverIds([]);
                    setImportStats(null);
                }}
                onConfirm={handleSendConsentForms}
                isSending={isSendingConsent}
                totalImported={importStats?.totalImported || 0}
                driversWithEmail={importStats?.driversWithEmail || 0}
                driversWithoutEmail={importStats?.driversWithoutEmail || 0}
            />
        </>
    );
};

export default CSVImportModal;
